/**
 * The reports of approved hours made so far (#292/#295, owner 2026-11-04): Kontroll's report dialog lists those
 * of its period, Rapporter's Lagrede rapporter every one. Each shows the period, the records and people, who
 * made it and when, and «Last ned igjen», which takes the kept text from GET /admin/report-receipts/:id. A
 * receipt from before the text was kept says so and has no button; a refusal (410, 409) shows the server's
 * sentence on its row. Needs timesheet.view, as making one does.
 */
import { errorText, saveFile } from '@/api/client';
import { type ReportReceiptRow, useReportReceipts } from '@/api/shifts';
import { useCan } from '@/auth/caps';
import { T } from '@/i18n';
import { Button, Skeleton } from '@/ui';
import { Icon } from '@/ui/icons';
import { useState } from 'react';
import { dateLong, dateTimeLong } from './kfmt';
import './reportReceipts.css';

const periodOf = (r: ReportReceiptRow) =>
  `${dateLong(r.period_start)} – ${dateLong(r.period_end)} ${r.period_end.slice(0, 4)}`;

export function ReportReceipts({ from, to, title }: { from?: string; to?: string; title: string }) {
  const can = useCan();
  const may = can('timeliste.se');
  const q = useReportReceipts(from, to, may);
  const [busy, setBusy] = useState<number | null>(null);
  const [refused, setRefused] = useState<Record<number, string>>({});
  if (!may) return null;
  const list = q.data?.receipts ?? [];
  const take = async (r: ReportReceiptRow) => {
    setBusy(r.id);
    setRefused(({ [r.id]: _, ...rest }) => rest);
    try {
      await saveFile(`/admin/report-receipts/${r.id}`, `timeliste-${r.period_start}-${r.period_end}.csv`);
    } catch (e) {
      setRefused((m) => ({ ...m, [r.id]: errorText(e) }));
    } finally {
      setBusy(null);
    }
  };
  return (
    <section className="rreceipts" data-testid="report-receipts" aria-label={title}>
      <h3>{title}</h3>
      {q.isPending ? (
        <Skeleton rows={2} />
      ) : q.isError ? (
        <p role="alert">{T('Kunne ikke hente rapportene: {why}', { why: errorText(q.error) })}</p>
      ) : list.length === 0 ? (
        <p>{T('Ingen rapporter over godkjente timer ennå.')}</p>
      ) : (
        <ul>
          {list.map((r) => (
            <li key={r.id} data-testid="report-receipt">
              <span>
                <b>{periodOf(r)}</b>
                {r.scope_label && <small>{r.scope_label}</small>}
                <small>
                  {T('{n, plural, one {# registrering} other {# registreringer}}', { n: r.record_count })},{' '}
                  {T('{n, plural, one {# ansatt} other {# ansatte}}', { n: r.people_count })}
                </small>
                <small>
                  {T('Laget {when} av {who}', {
                    when: dateTimeLong(r.prepared_at) ?? '',
                    who: r.prepared_by_name,
                  })}
                </small>
                {!r.has_content && (
                  <small>
                    {T(
                      'Filen ble ikke tatt vare på da rapporten ble laget. Lag rapporten på nytt fra Kontroll.',
                    )}
                  </small>
                )}
                {refused[r.id] && (
                  <span className="rrefused" role="alert">
                    {refused[r.id]}
                  </span>
                )}
              </span>
              {r.has_content && (
                <Button
                  small
                  onClick={() => take(r)}
                  disabled={busy === r.id}
                  aria-label={`${T('Last ned igjen')} ${periodOf(r)}`}
                >
                  <Icon name="download" />
                  {T('Last ned igjen')}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
